import {
  StatusFilterType,
  statusMessages,
} from '@app/modules/logger/screens/status/components/options';
import SensorsIcon from '@mui/icons-material/Sensors';
import SensorsOffIcon from '@mui/icons-material/SensorsOff';
import { ToggleButtonGroup } from '@mui/material';
import { StatusFilterToggleButton } from './StatusFilterToggleButton';
import { useMemo } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import { getTintedSelectedToggleSx } from '../../helpers/status-toggle-button-styles';
import { useStatusQueryFilter } from '../../hooks/useStatusQueryFilter';
import { StationStatus } from '../../types';

const statusActivityGroupMessage = defineMessage({
  id: 'logger.statusScreen.statusActivity',
  defaultMessage: 'Estado',
});

const STATUS_COLORS = {
  online: '#2E7D32',
  offline: '#ec0864',
} as const;

const STATUS_ACTIVITY_FILTERS = [
  {
    type: StatusFilterType.ONLINE,
    icon: SensorsIcon,
    message: statusMessages.online,
    selectedIconColor: STATUS_COLORS.online,
  },
  {
    type: StatusFilterType.OFFLINE,
    icon: SensorsOffIcon,
    message: statusMessages.offline,
    selectedIconColor: STATUS_COLORS.offline,
  },
] as const;

const ALL_STATUS_ACTIVITY_FILTERS = STATUS_ACTIVITY_FILTERS.map(({ type }) => type);

/**
 * Non-exclusive toggle group for online/offline activity.
 * No status param = show all (both options selected in UI).
 */
export function StatusActivityFilter({
  statuses,
  isLoading = false,
}: {
  statuses: StationStatus[];
  isLoading?: boolean;
}) {
  const { $t } = useIntl();
  const { queryParams, setQueryParams } = useStatusQueryFilter();

  const counts = useMemo(
    () => ({
      online: statuses.filter((s) => s.status === 'online').length,
      offline: statuses.filter((s) => s.status === 'offline').length,
    }),
    [statuses],
  );

  const selectedStatusTypes = useMemo(() => {
    if (queryParams.status == null) {
      return ALL_STATUS_ACTIVITY_FILTERS;
    }

    const filteredSelection = queryParams.status.filter((type) =>
      ALL_STATUS_ACTIVITY_FILTERS.includes(type),
    );

    return filteredSelection.length > 0 ? filteredSelection : ALL_STATUS_ACTIVITY_FILTERS;
  }, [queryParams.status]);

  const handleChange = (_event: React.MouseEvent<HTMLElement>, newValue: StatusFilterType[]) => {
    if (newValue.length === 0 || newValue.length === ALL_STATUS_ACTIVITY_FILTERS.length) {
      setQueryParams({ status: null });
    } else {
      setQueryParams({ status: newValue });
    }
  };

  return (
    <ToggleButtonGroup
      value={selectedStatusTypes}
      onChange={handleChange}
      aria-label={$t(statusActivityGroupMessage)}
      size="small"
      disabled={isLoading}
    >
      {STATUS_ACTIVITY_FILTERS.map(({ type, icon, message, selectedIconColor }) => (
        <StatusFilterToggleButton
          key={type}
          value={type}
          icon={icon}
          label={$t(message)}
          count={counts[type]}
          sx={getTintedSelectedToggleSx(selectedIconColor)}
        />
      ))}
    </ToggleButtonGroup>
  );
}
